/**
 * 🚗 CREATE VEHICLE BOOKING DIALOG
 * Dialog for creating vehicle rental and airport transfer bookings
 * Supports both booking types from a single interface
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Stack,
  Divider,
  Autocomplete,
  RadioGroup,
  Radio,
  Card,
  CardContent,
} from '@mui/material';
import { DirectionsCar, Flight, CalendarToday, LocationOn, AttachMoney, Person, Business } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import CustomerSelector from './CustomerSelector';
import GuestCustomerDialog from './GuestCustomerDialog';
import BookingCostPreview from './BookingCostPreview';
import VehicleSelector from './VehicleSelector';
import adminBookingService, {
  VehicleRentalBookingRequest,
  AirportTransferBookingRequest,
  CalculateCostsRequest,
  CostCalculation,
} from '@/services/adminBookingService';
import { simplBookingService } from '@/services/bookingService';
import { vehicleAPI } from '@/services/vehicleAPI';
import driverService from '@/services/driverService';
import { airportService, Airport } from '@/services/airportService';
import { airportZoneService, AirportZone } from '@/services/airportZoneService';
import { User } from '@/services/userService';
import { paystackPaymentService } from '@/services/paystackPaymentService';
import { CorporateAccountSelector } from '@/components/corporate/CorporateAccountSelector';
import { CorporateAccount } from '@/services/corporateAccountsService';
import {
  // validateCorporateBooking, // Removed - credit line is a future feature
  validateBookingCustomer,
} from '@/utils/corporateBookingValidation';

interface CreateVehicleBookingDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialType?: 'rental' | 'airport-transfer';
}

export const CreateVehicleBookingDialog: React.FC<CreateVehicleBookingDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialType = 'rental',
}) => {
  // Booking type
  const [bookingType, setBookingType] = useState<'rental' | 'airport-transfer'>(initialType);

  // Booking customer type (Individual vs Corporate)
  const [bookingCustomerType, setBookingCustomerType] = useState<'individual' | 'corporate'>('individual');

  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [guestCustomerDialogOpen, setGuestCustomerDialogOpen] = useState(false);

  // Corporate Account
  const [corporateAccountId, setCorporateAccountId] = useState<string | null>(null);
  const [selectedCorporateAccount, setSelectedCorporateAccount] = useState<CorporateAccount | null>(null);
  // Note: Credit line validation removed - credit line is a future feature
  // Corporate bookings currently pay like individual customers (payment links, etc.)

  // Vehicle selection
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);

  // Driver selection (for chauffeur-driven rentals)
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string>('');

  // Airport/Zone (for airport transfers)
  const [airports, setAirports] = useState<Airport[]>([]);
  const [selectedAirport, setSelectedAirport] = useState<string>('');
  const [zones, setZones] = useState<AirportZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [zoneVehicleCounts, setZoneVehicleCounts] = useState<Record<string, number>>({});

  // Date/Time
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('18:00'); // Default to 6 PM for 8-hour rental
  const [hasPastDate, setHasPastDate] = useState(false);
  const [showPastDateConfirmation, setShowPastDateConfirmation] = useState(false);

  // Rental option (service type)
  const [rentalOption, setRentalOption] = useState<'self_drive' | 'with_driver'>('with_driver');

  // Locations
  const [pickupLocation, setPickupLocation] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');

  // Airport transfer direction
  const [transferDirection, setTransferDirection] = useState<'to-airport' | 'from-airport'>('from-airport');

  // Add-ons
  const [insurance, setInsurance] = useState(false);
  const [insuranceCost, setInsuranceCost] = useState(0);
  const [delivery, setDelivery] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [specialRequests, setSpecialRequests] = useState('');

  // Payment
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentOption, setPaymentOption] = useState<'send-link' | 'pay-now'>('send-link');
  const [paymentLinkDialog, setPaymentLinkDialog] = useState({ open: false, link: '', bookingId: '', email: '' });

  // Admin overrides
  const [adminNotes, setAdminNotes] = useState('');
  const [skipAvailabilityCheck, setSkipAvailabilityCheck] = useState(false);

  // Cost calculation
  const [costCalculation, setCostCalculation] = useState<CostCalculation | null>(null);
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (open && bookingType === 'airport-transfer') {
      loadAirports();
    }
  }, [open, bookingType]);

  // Load zones when airport changes
  useEffect(() => {
    if (selectedAirport) {
      loadZones(selectedAirport);
    } else {
      setZones([]);
      setSelectedZone('');
    }
  }, [selectedAirport]);

  // Load drivers on mount for vehicle rentals
  useEffect(() => {
    if (open && bookingType === 'rental') {
      loadDrivers();
    }
  }, [open, bookingType]);

  // Auto-set pickup/dropoff for airport transfers based on direction
  useEffect(() => {
    if (bookingType === 'airport-transfer' && selectedAirport) {
      const airport = airports.find(a => a.id === selectedAirport);
      const airportLocation = airport ? `${airport.airportCode} - ${airport.airportName}` : '';

      if (transferDirection === 'from-airport') {
        setPickupLocation(airportLocation);
      } else {
        setDropoffLocation(airportLocation);
      }
    }
  }, [bookingType, selectedAirport, transferDirection, airports]);

  // Calculate costs when relevant fields change
  useEffect(() => {
    if (shouldCalculateCost()) {
      calculateCosts();
    }
  }, [
    bookingType,
    selectedCustomer,
    corporateAccountId,
    selectedVehicle,
    selectedAirport,
    selectedZone,
    transferDirection,
    startDate,
    startTime,
    endDate,
    endTime,
    rentalOption,
    selectedDriver,
    insurance,
    insuranceCost,
    delivery,
    deliveryCost,
  ]);

  // Check if start date is in the past
  useEffect(() => {
    if (!startDate) {
      setHasPastDate(false);
      return;
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for fair comparison

    const selectedDate = new Date(startDate);
    const isPastDate = selectedDate < now;

    setHasPastDate(isPastDate);
  }, [startDate]);

  const loadDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const response = await driverService.getDrivers({} as any, 1, 100);
      setDrivers(response.drivers || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const loadAirports = async () => {
    try {
      const response = await airportService.getAirports();
      if (response.success && response.airports) {
        setAirports(response.airports);
      }
    } catch (error) {
      console.error('Error loading airports:', error);
    }
  };

  const loadZones = async (airportId: string) => {
    try {
      const response = await airportZoneService.getZones(airportId);
      if (response.success && response.zones) {
        setZones(response.zones);

        // Load vehicle counts for each zone
        loadZoneVehicleCounts(airportId, response.zones);
      }
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  };

  const loadZoneVehicleCounts = async (airportId: string, zones: AirportZone[]) => {
    try {
      const counts: Record<string, number> = {};

      // Fetch vehicle counts for each zone in parallel
      await Promise.all(
        zones.map(async (zone) => {
          try {
            const response = await vehicleAPI.getVehiclesWithAirportPricing({
              hasPricing: true,
              airportId: airportId,
              zoneId: zone.id,
              limit: 1000, // Get all vehicles to count them
            });
            counts[zone.id] = response.vehicles?.length || 0;
          } catch (error) {
            console.error(`Error loading vehicle count for zone ${zone.id}:`, error);
            counts[zone.id] = 0;
          }
        })
      );

      setZoneVehicleCounts(counts);
    } catch (error) {
      console.error('Error loading zone vehicle counts:', error);
    }
  };

  const shouldCalculateCost = (): boolean => {
    // Must have either individual customer or corporate account
    const hasCustomer = bookingCustomerType === 'individual' ? !!selectedCustomer : !!corporateAccountId;
    if (!hasCustomer || !selectedVehicle || !startDate) return false;

    if (bookingType === 'rental') {
      return !!endDate;
    } else {
      return !!selectedAirport && !!selectedZone;
    }
  };

  const calculateCosts = async () => {
    // Need either customer or corporate account for cost calculation
    const hasCustomer = bookingCustomerType === 'individual' ? !!selectedCustomer : !!corporateAccountId;
    if (!hasCustomer || !selectedVehicle) return;

    // Validate dates for rentals
    if (bookingType === 'rental' && startDate && endDate) {
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${endDate}T${endTime}`);

      if (end < start) {
        setCostError('End date/time cannot be before start date/time');
        setCostCalculation(null);
        return;
      }
    }

    setCalculatingCost(true);
    setCostError(null);

    try {
      const vehicleId = selectedVehicle?.id || selectedVehicle?.vehicle_id;

      // For airport transfers, calculate end time as start time + 2 hours
      let calculatedEndDate: string;
      if (bookingType === 'rental') {
        calculatedEndDate = `${endDate}T${endTime}:00Z`;
      } else {
        // Airport transfer: add 2 hours to start time
        const startDateTime = new Date(`${startDate}T${startTime}:00`);
        const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000); // +2 hours
        calculatedEndDate = endDateTime.toISOString();
      }

      const request: CalculateCostsRequest = {
        // Send EITHER customerId OR corporateAccountId (not both)
        customerId: bookingCustomerType === 'individual' ? selectedCustomer?.id : undefined,
        corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
        vehicleId: vehicleId,
        startDate: `${startDate}T${startTime}:00Z`,
        endDate: calculatedEndDate,
        serviceCategory: bookingType === 'rental' ? 'vehicle-rental' : 'airport_transfer',
      };

      if (bookingType === 'rental') {
        request.bookingType = rentalOption; // 'self_drive' or 'with_driver'
      } else {
        request.airportId = selectedAirport;
        request.zoneId = selectedZone;
        // Map transfer direction to trip type
        request.tripType = transferDirection === 'from-airport' ? 'airport_pickup' : 'airport_dropoff';
      }

      const response = await adminBookingService.calculateCosts(request);

      if (response.success && response.data) {
        // 🔍 Log backend response to verify _metadata
        console.log('📊 Backend cost calculation response:', {
          _metadata: response.data._metadata,
          bookingFeeRate: response.data._metadata?.bookingFeeRate,
          hostServiceFeePercentage: response.data._metadata?.hostServiceFeePercentage,
          vatPercentage: response.data._metadata?.vatPercentage
        });

        // Transform backend response to match component expectations
        const transformedData = {
          // Keep original data first
          ...response.data,
          // Then override with transformed fields
          totalCost: response.data.totalDue || response.data.totalCost || 0,
          costBreakdown: response.data.breakdown || response.data.costBreakdown || [],
        };
        setCostCalculation(transformedData);
      } else {
        setCostError(
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Failed to calculate costs'
        );
        setCostCalculation(null);
      }
    } catch (error) {
      console.error('Error calculating costs:', error);
      setCostError('Failed to calculate costs');
      setCostCalculation(null);
    } finally {
      setCalculatingCost(false);
    }
  };

  const handleSubmit = async () => {
    // Validate customer/corporate account selection
    const customerValidation = validateBookingCustomer(
      selectedCustomer?.id,
      corporateAccountId || undefined
    );
    if (!customerValidation.isValid) {
      toast.error(customerValidation.error || 'Please select a customer or corporate account');
      return;
    }

    // Note: Corporate credit validation removed - credit line is a future feature
    // Corporate bookings currently pay via payment links like individual customers
    // TODO: Re-enable this validation when credit line feature is implemented
    // if (bookingCustomerType === 'corporate' && corporateAccountId) {
    //   const validation = validateCorporateBooking(
    //     selectedCorporateAccount,
    //     costCalculation?.totalCost || 0,
    //     true
    //   );
    //
    //   if (!validation.isValid) {
    //     toast.error(validation.errors.join(', '));
    //     return;
    //   }
    //
    //   // Show warnings
    //   validation.warnings.forEach(warning => toast.warning(warning));
    // }
    if (!selectedVehicle) {
      toast.error('Please select a vehicle');
      return;
    }
    if (!startDate) {
      toast.error('Please select start date');
      return;
    }
    if (bookingType === 'rental' && !endDate) {
      toast.error('Please select end date');
      return;
    }
    if (bookingType === 'airport-transfer' && (!selectedAirport || !selectedZone)) {
      toast.error('Please select airport and zone');
      return;
    }
    if (!pickupLocation) {
      toast.error('Please enter pickup location');
      return;
    }
    if (!costCalculation) {
      toast.error('Cost calculation failed. Please check your inputs.');
      return;
    }

    // Check for past date and show confirmation
    if (hasPastDate) {
      setShowPastDateConfirmation(true);
      return;
    }

    await performSubmit();
  };

  const performSubmit = async () => {
    setSubmitting(true);


    try {
      let bookingId;
      let bookingNumber;
      const adminId = localStorage.getItem('userId') || 'admin';

      // Get customer details - for corporate bookings, use contact person details
      const customerEmail = bookingCustomerType === 'individual'
        ? (selectedCustomer?.personalInfo?.email || (selectedCustomer as any)?.email || '')
        : (selectedCorporateAccount?.contactPerson?.email || '');
      const customerName = bookingCustomerType === 'individual'
        ? (selectedCustomer?.personalInfo?.fullName || (selectedCustomer as any)?.name || '')
        : (selectedCorporateAccount?.contactPerson?.name || selectedCorporateAccount?.companyName || '');
      const customerPhone = bookingCustomerType === 'individual'
        ? (selectedCustomer?.personalInfo?.phoneNumber || (selectedCustomer as any)?.phone || '')
        : (selectedCorporateAccount?.contactPerson?.phone || '');

      // Use old working endpoint that has Paystack configured
      if (bookingType === 'rental') {
        const vehicleId = selectedVehicle?.id || selectedVehicle?.vehicle_id;
        const isSelfDrive = rentalOption === 'self_drive';

        // Extract cost breakdown items
        const rentalCostItem = costCalculation.costBreakdown?.find(item => item.type === 'rental');
        const bookingFeeItem = costCalculation.costBreakdown?.find(item => item.label.includes('Booking Fee'));
        const serviceFeeItem = costCalculation.costBreakdown?.find(item => item.label.includes('Service Fee'));
        const vatItem = costCalculation.costBreakdown?.find(item => item.label.includes('VAT'));
        const securityDepositItem = costCalculation.costBreakdown?.find(item => item.type === 'deposit');

        const bookingData = {
          userId: bookingCustomerType === 'individual' ? selectedCustomer?.id : undefined,
          corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
          vehicleId: vehicleId,
          startDate: new Date(`${startDate}T${startTime}:00Z`).toISOString(),
          endDate: new Date(`${endDate}T${endTime}:00Z`).toISOString(),
          startTime: startTime || '10:00',
          endTime: endTime || '10:00',
          pickupLocation: pickupLocation || 'Not specified',
          dropoffLocation: dropoffLocation || pickupLocation || 'Not specified',

          totalAmount: costCalculation.totalCost,
          dailyRate: rentalCostItem?.amount || 0,
          bookingFee: bookingFeeItem?.amount || 0,
          securityDeposit: securityDepositItem?.amount || 0,

          // Full cost breakdown for backend payment processing
          // ✅ Use rates from backend calculation (_metadata) - NO fallbacks to prevent using wrong values
          costBreakdown: {
            rentalCost: rentalCostItem?.amount || 0,
            bookingFee: bookingFeeItem?.amount || 0,
            bookingFeeRate: costCalculation._metadata?.bookingFeeRate, // Must come from backend
            serviceFee: serviceFeeItem?.amount || 0,
            serviceFeeRate: costCalculation._metadata?.hostServiceFeePercentage, // Must come from backend
            vat: vatItem?.amount || 0,
            vatRate: costCalculation._metadata?.vatPercentage || 7.5, // From backend or legal standard
            securityDeposit: securityDepositItem?.amount || 0,
            totalAmount: costCalculation.totalCost,
            hostPayout: rentalCostItem?.amount || 0 // Simplified - TODO: calculate properly
          },

          serviceCategory: 'vehicle-rental',
          serviceType: isSelfDrive ? 'self-drive' : 'chauffeur-driven',
          bookingType: 'admin_created',
          // The API sets the status from the payment status (paid → confirmed)
          paymentStatus,
          paymentMethod: paymentMethod || undefined,
          customerName: customerName,
          customerEmail: customerEmail,
          customerPhone: customerPhone,
          notes: adminNotes || `Admin-created ${isSelfDrive ? 'Self-Drive' : 'With Driver'} booking`,
          adminId: adminId
        };

        const createBookingResponse = await simplBookingService.createAdminBooking(bookingData);

        if (!createBookingResponse || !createBookingResponse._id) {
          throw new Error('Failed to create booking - no booking ID returned');
        }

        bookingId = createBookingResponse._id;
        bookingNumber = createBookingResponse.bookingNumber || '';
      } else {
        // For airport transfers, still use new endpoint for now (TODO: migrate to old endpoint)
        const vehicleId = selectedVehicle?.id || selectedVehicle?.vehicle_id;

        // For corporate bookings, use the account owner's ID as customerId (backend workaround for airport transfers)
        // TODO: Backend should accept corporateAccountId without requiring customerId
        const effectiveCustomerId = bookingCustomerType === 'individual'
          ? selectedCustomer?.id
          : selectedCorporateAccount?.owner?.userId;

        const request: AirportTransferBookingRequest = {
          customerId: effectiveCustomerId,
          corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
          vehicleId: vehicleId,
          airportId: selectedAirport,
          zoneId: selectedZone,
          tripType: transferDirection === 'from-airport' ? 'airport_pickup' : 'airport_dropoff',
          startDate: `${startDate}T${startTime}:00Z`,
          pickupLocation,
          dropoffLocation: dropoffLocation || undefined,
          driverId: selectedDriver || undefined,
          serviceCategory: 'airport_transfer',
          insurance,
          insuranceCost: insurance ? insuranceCost : undefined,
          specialRequests: specialRequests || undefined,
          totalAmount: costCalculation.totalCost,
          securityDeposit: costCalculation.securityDeposit?.amount || costCalculation.costBreakdown?.find(
            (item) => item.type === 'deposit'
          )?.amount,
          paymentStatus,
          paymentMethod: paymentMethod || undefined,
          adminNotes: adminNotes || undefined,
          skipAvailabilityCheck,
        };

        const response = await adminBookingService.createAirportTransferBooking(request);

        if (!response.success) {
          throw new Error(response.error || 'Failed to create airport transfer booking');
        }

        bookingId = response.data?.id || response.data?.bookingId;
        bookingNumber = response.data?.bookingNumber || '';
      }

      if (bookingId) {

        // Handle payment based on selected option
        // A booking already marked paid needs no payment link
        if (paymentStatus === 'paid') {
          // nothing to collect
        } else if (paymentOption === 'send-link') {
          try {
            // Initialize payment with Paystack

            // Validate email before payment initialization
            if (!customerEmail || !customerEmail.includes('@')) {
              toast.error('Customer email is required for payment link generation');
              onSuccess();
              handleClose();
              return;
            }

            // Log all payment initialization parameters
            const paymentData = {
              amount: costCalculation.totalCost,
              email: customerEmail,
              channel: 'card' as const,
              metadata: {
                bookingId: bookingId,
                userId: bookingCustomerType === 'individual' ? selectedCustomer?.id : undefined,
                corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
                bookingType: 'admin_created' as const,
                adminId: adminId
              }
            };


            const paymentResponse = await paystackPaymentService.initializePayment(paymentData);


            const paymentLink = paymentResponse.data.authorizationUrl || paymentResponse.data.authorization_url;

            // Show payment link dialog
            setPaymentLinkDialog({
              open: true,
              link: paymentLink,
              bookingId: bookingId,
              email: customerEmail
            });

            toast.success(`Booking created! Booking #${bookingNumber}`);
            onSuccess();
            // Don't close dialog yet - wait for user to close payment link modal
          } catch (error) {
            console.error('Error initializing payment:', error);
            toast.error('Booking created but failed to generate payment link');
            onSuccess();
            handleClose();
          }
        } else if (paymentOption === 'pay-now') {
          // TODO: Implement pay-now flow with Paystack popup
          toast.success(`Booking created successfully! Booking #${bookingNumber}`);
          onSuccess();
          handleClose();
        } else {
          toast.success(`Booking created successfully! Booking #${bookingNumber}`);
          onSuccess();
          handleClose();
        }
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create booking';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setBookingType(initialType);
    setBookingCustomerType('individual');
    setSelectedCustomer(null);
    setCorporateAccountId(null);
    setSelectedCorporateAccount(null);
    setSelectedVehicle('');
    setSelectedDriver('');
    setSelectedAirport('');
    setSelectedZone('');
    setStartDate('');
    setStartTime('10:00');
    setEndDate('');
    setEndTime('18:00');
    setRentalOption('chauffeur-driven');
    setPickupLocation('');
    setDropoffLocation('');
    setTransferDirection('from-airport');
    setInsurance(false);
    setInsuranceCost(0);
    setDelivery(false);
    setDeliveryAddress('');
    setDeliveryCost(0);
    setSpecialRequests('');
    setPaymentStatus('pending');
    setPaymentMethod('');
    setAdminNotes('');
    setSkipAvailabilityCheck(false);
    setCostCalculation(null);
    setCostError(null);
    onClose();
  };

  const isWithDriver = rentalOption === 'with_driver';
  const isSelfDrive = rentalOption === 'self_drive';

  // Helper to check if customer is fully verified
  const isCustomerVerified = (customer: User | null) => {
    if (!customer) return false;

    const flatCustomer = customer as any;

    // Backend /api/v1/admin/users returns isVerified boolean field
    // This is the authoritative source for customer verification status
    return flatCustomer.isVerified === true;
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <DirectionsCar />
          <Typography variant="h6">Create Booking</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Booking Type Tabs */}
          <Tabs
            value={bookingType}
            onChange={(_, value) => setBookingType(value)}
            sx={{ mb: 3 }}
          >
            <Tab
              icon={<DirectionsCar />}
              label="Vehicle Rental"
              value="rental"
              iconPosition="start"
            />
            <Tab
              icon={<Flight />}
              label="Airport Transfer"
              value="airport-transfer"
              iconPosition="start"
            />
          </Tabs>

          <Grid container spacing={3}>
            {/* Customer Type Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                1. Who is this booking for?
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <RadioGroup
                    value={bookingCustomerType}
                    onChange={(e) => {
                      const newType = e.target.value as 'individual' | 'corporate';
                      setBookingCustomerType(newType);
                      // Clear selections when switching
                      if (newType === 'individual') {
                        setCorporateAccountId(null);
                        setSelectedCorporateAccount(null);
                      } else {
                        setSelectedCustomer(null);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Individual Customer
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Regular customer booking
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: bookingCustomerType === 'individual' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    />
                    <FormControlLabel
                      value="corporate"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Corporate Account
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Business account with credit limit
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: bookingCustomerType === 'corporate' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Individual Customer Selection */}
            {bookingCustomerType === 'individual' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  2. Select Customer
                </Typography>
                <CustomerSelector
                  selectedCustomer={selectedCustomer}
                  onSelectCustomer={setSelectedCustomer}
                  onCreateNew={() => setGuestCustomerDialogOpen(true)}
                  required
                />

                {/* Unverified Customer Warning */}
                {selectedCustomer && !isCustomerVerified(selectedCustomer) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Unverified Customer - Proceed with Caution
                    </Typography>
                    <Typography variant="caption" display="block">
                      This customer has not completed identity verification (KYC). They are technically not allowed to book rentals.
                      Admin override is required to proceed with this booking.
                    </Typography>
                  </Alert>
                )}
              </Grid>
            )}

            {/* Corporate Account Selection */}
            {bookingCustomerType === 'corporate' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  2. Select Corporate Account
                </Typography>
                {/* Note: Credit line is a future feature. Corporate bookings currently pay via payment links like individual customers. */}
                <CorporateAccountSelector
                  value={corporateAccountId}
                  onChange={(accountId, account) => {
                    setCorporateAccountId(accountId);
                    setSelectedCorporateAccount(account);
                  }}
                  bookingAmount={costCalculation?.totalCost || 0}
                  // Note: Credit validation removed - credit line is a future feature
                  disabled={submitting}
                />
              </Grid>
            )}

            {/* Airport & Zone Selection - BEFORE vehicle (for airport transfers only) */}
            {bookingType === 'airport-transfer' && (
              <>
                {/* Transfer Direction */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    3. Transfer Direction
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Direction</InputLabel>
                    <Select
                      value={transferDirection}
                      label="Direction"
                      onChange={(e: any) => setTransferDirection(e.target.value)}
                    >
                      <MenuItem value="from-airport">From Airport (Arrival - Pickup from Airport)</MenuItem>
                      <MenuItem value="to-airport">To Airport (Departure - Dropoff at Airport)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date & Time for Airport Transfers */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    4. Date & Time
                  </Typography>
                </Grid>
                {hasPastDate && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      ⚠️ <strong>Past Date Selected:</strong> You are creating a booking with a start date in the past.
                      This should only be done for historical records or special circumstances. Please confirm this is intentional.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Transfer Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                    <TextField
                      fullWidth
                      type="time"
                      label="Transfer Time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    5. Select Airport
                  </Typography>
                  <FormControl fullWidth required>
                    <InputLabel>Airport</InputLabel>
                    <Select
                      value={selectedAirport}
                      label="Airport"
                      onChange={(e) => {
                        setSelectedAirport(e.target.value);
                        // Clear zone and vehicle when airport changes
                        setSelectedZone('');
                        setSelectedVehicle(null);
                      }}
                    >
                      {airports.map((airport) => (
                        <MenuItem key={airport.id} value={airport.id}>
                          {airport.airportCode} - {airport.airportName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    6. Select Zone
                  </Typography>
                  <FormControl fullWidth required disabled={!selectedAirport}>
                    <InputLabel>Zone</InputLabel>
                    <Select
                      value={selectedZone}
                      label="Zone"
                      onChange={(e) => {
                        setSelectedZone(e.target.value);
                        // Clear vehicle when zone changes
                        setSelectedVehicle(null);
                      }}
                    >
                      {zones.map((zone) => {
                        // Handle different backend response formats
                        const zoneData = zone as any;
                        let distanceDisplay = zone.distanceRange?.display;

                        // If display field not present, construct from min/max
                        if (!distanceDisplay && (zoneData.minDistanceKm !== undefined || zoneData.maxDistanceKm !== undefined)) {
                          const min = zoneData.minDistanceKm || 0;
                          const max = zoneData.maxDistanceKm || 0;
                          distanceDisplay = `${min}-${max}km`;
                        }

                        const vehicleCount = zoneVehicleCounts[zone.id];
                        const countDisplay = vehicleCount !== undefined ? ` - ${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''}` : '';

                        return (
                          <MenuItem key={zone.id} value={zone.id}>
                            {zone.zoneName} {distanceDisplay ? `(${distanceDisplay})` : ''}{countDisplay}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {selectedAirport && zones.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      No zones configured for this airport
                    </Typography>
                  )}
                </Grid>
              </>
            )}

            {/* Rental Option (for vehicle rental only) */}
            {bookingType === 'rental' && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  3. Rental Option
                </Typography>
                <FormControl fullWidth required>
                  <InputLabel>Rental Option</InputLabel>
                  <Select
                    value={rentalOption}
                    label="Service Type"
                    onChange={(e: any) => {
                      const newOption = e.target.value;
                      const oldOption = rentalOption;
                      setRentalOption(newOption);

                      // Clear driver selection when switching to with_driver (host assigns driver)
                      if (newOption === 'with_driver') {
                        setSelectedDriver('');
                      }

                      // Clear vehicle selection when switching between self-drive and chauffeur-driven
                      // since available vehicles are different
                      const switchingServiceType = oldOption !== newOption;

                      if (switchingServiceType && selectedVehicle) {
                        setSelectedVehicle(null);
                        toast('Please select a new vehicle for this service type');
                      }
                    }}
                  >
                    <MenuItem value="self_drive">Self-Drive</MenuItem>
                    <MenuItem value="with_driver">With Driver (Chauffeur Driven)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* With Driver Rental Info */}
            {bookingType === 'rental' && isWithDriver && (
              <Grid item xs={12}>
                <Alert severity="info">
                  This vehicle rental includes a driver. The host will assign the driver after booking confirmation.
                </Alert>
              </Grid>
            )}

            {/* Driver Selection (only for self-drive rentals as optional add-on) */}
            {bookingType === 'rental' && isSelfDrive && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  5. Add Driver (Optional)
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add a driver as an optional add-on if the customer wants assistance.
                </Alert>
                <Autocomplete
                  options={drivers}
                  loading={loadingDrivers}
                  value={drivers.find(d => d.id === selectedDriver) || null}
                  onChange={(_, newValue) => {
                    setSelectedDriver(newValue ? newValue.id : '');
                  }}
                  getOptionLabel={(driver) => {
                    const firstName = driver.personalInfo?.firstName || driver.firstName || '';
                    const lastName = driver.personalInfo?.lastName || driver.lastName || '';
                    const rate = driver.pricing?.daily || driver.dailyRate || driver.personalInfo?.dailyRate || 0;
                    return `${firstName} ${lastName} - ₦${rate.toLocaleString()}/day`;
                  }}
                  renderOption={(props, driver) => (
                    <li {...props} key={driver.id}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                          {driver.personalInfo?.firstName || driver.firstName} {driver.personalInfo?.lastName || driver.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ₦{(driver.pricing?.daily || driver.dailyRate || driver.personalInfo?.dailyRate || 0).toLocaleString()}/day
                          {driver.personalInfo?.phone && ` • ${driver.personalInfo.phone}`}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Driver (Optional Add-on)"
                      helperText="Leave empty if customer will drive themselves"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDrivers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText="No drivers found"
                  filterOptions={(options, { inputValue }) => {
                    const query = inputValue.toLowerCase();
                    return options.filter(driver => {
                      const firstName = (driver.personalInfo?.firstName || driver.firstName || '').toLowerCase();
                      const lastName = (driver.personalInfo?.lastName || driver.lastName || '').toLowerCase();
                      const phone = (driver.personalInfo?.phone || driver.phone || '').toLowerCase();
                      return firstName.includes(query) || lastName.includes(query) || phone.includes(query);
                    });
                  }}
                />
              </Grid>
            )}

            {/* Date & Time (for rentals only - airport transfers have it earlier) */}
            {bookingType === 'rental' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    4. Date & Time
                  </Typography>
                </Grid>
                {hasPastDate && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      ⚠️ <strong>Past Date Selected:</strong> You are creating a booking with a start date in the past.
                      This should only be done for historical records or special circumstances. Please confirm this is intentional.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => {
                    const newStartDate = e.target.value;
                    setStartDate(newStartDate);
                    // Auto-fill end date for convenience (especially for same-day rentals)
                    if (!endDate && newStartDate) {
                      setEndDate(newStartDate);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate || undefined }}
                  required
                  helperText="Can be same day or later"
                />
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Stack>
            </Grid>
              </>
            )}

            {/* Locations */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {bookingType === 'rental' ? '5' : '7'}. Locations
              </Typography>
            </Grid>

            {/* Airport Transfer Locations */}
            {bookingType === 'airport-transfer' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={transferDirection === 'from-airport' ? 'Customer Dropoff Address' : 'Customer Pickup Address'}
                  value={transferDirection === 'from-airport' ? dropoffLocation : pickupLocation}
                  onChange={(e) => {
                    const address = e.target.value;
                    if (transferDirection === 'from-airport') {
                      setDropoffLocation(address);
                    } else {
                      setPickupLocation(address);
                    }
                  }}
                  required
                  placeholder="Enter customer's address"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {transferDirection === 'from-airport'
                    ? `Pickup from: ${airports.find(a => a.id === selectedAirport)?.airportName || 'Airport'}`
                    : `Dropoff at: ${airports.find(a => a.id === selectedAirport)?.airportName || 'Airport'}`
                  }
                </Typography>
              </Grid>
            )}

            {/* Rental Locations */}
            {bookingType === 'rental' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pickup Location"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Dropoff Location (Optional)"
                    value={dropoffLocation}
                    onChange={(e) => setDropoffLocation(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Vehicle Selection */}
            <Grid item xs={12} md={bookingType === 'rental' ? 6 : 12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {bookingType === 'rental' ? '6' : '8'}. Select Vehicle
              </Typography>
              {bookingType === 'airport-transfer' && (!selectedAirport || !selectedZone) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please select airport and zone first to see available vehicles
                </Alert>
              )}
              <VehicleSelector
                selectedVehicle={selectedVehicle}
                onSelectVehicle={setSelectedVehicle}
                rentalOption={bookingType === 'rental' ? rentalOption : undefined}
                bookingType={bookingType}
                airportId={bookingType === 'airport-transfer' ? selectedAirport : undefined}
                zoneId={bookingType === 'airport-transfer' ? selectedZone : undefined}
                label="Select Vehicle"
                required
                disabled={bookingType === 'airport-transfer' && (!selectedAirport || !selectedZone)}
              />
            </Grid>

            {/* Add-ons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {bookingType === 'rental' ? '7' : '9'}. Add-ons
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch checked={insurance} onChange={(e) => setInsurance(e.target.checked)} />
                }
                label="Add Insurance"
              />
              {insurance && (
                <TextField
                  fullWidth
                  type="number"
                  label="Insurance Cost"
                  value={insuranceCost}
                  onChange={(e) => setInsuranceCost(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
            {bookingType === 'rental' && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch checked={delivery} onChange={(e) => setDelivery(e.target.checked)} />
                  }
                  label="Add Delivery Service"
                />
                {delivery && (
                  <>
                    <TextField
                      fullWidth
                      label="Delivery Address"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      sx={{ mt: 1, mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Delivery Cost"
                      value={deliveryCost}
                      onChange={(e) => setDeliveryCost(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                    />
                  </>
                )}
              </Grid>
            )}
            {bookingType === 'airport-transfer' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Special Requests (Optional)"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            {/* Payment */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {bookingType === 'rental' ? '8' : '10'}. Payment
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={(e: any) => setPaymentStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Method (Optional)"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="e.g., Card, Cash, Bank Transfer"
              />
            </Grid>

            {/* Payment Option Selection */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    How to process payment?
                  </Typography>
                  <RadioGroup
                    value={paymentOption}
                    onChange={(e) => setPaymentOption(e.target.value as 'send-link' | 'pay-now')}
                  >
                    <FormControlLabel
                      value="send-link"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Send Payment Link
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Create booking and email payment link to customer
                          </Typography>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: paymentOption === 'send-link' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    />
                    <FormControlLabel
                      value="pay-now"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Process Payment Now
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Open Paystack popup for immediate payment
                          </Typography>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: paymentOption === 'pay-now' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Admin Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {bookingType === 'rental' ? '9' : '11'}. Admin Options
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                multiline
                rows={2}
                placeholder="Internal notes about this booking..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={skipAvailabilityCheck}
                    onChange={(e) => setSkipAvailabilityCheck(e.target.checked)}
                  />
                }
                label="Skip Availability Check (Override conflicts)"
              />
            </Grid>

            {/* Cost Preview */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Cost Preview
              </Typography>
              <BookingCostPreview
                calculation={costCalculation}
                loading={calculatingCost}
                error={costError}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting ||
            !costCalculation
            // Note: Credit line validation removed - corporate bookings pay like individual customers
          }
          startIcon={submitting && <CircularProgress size={16} />}
        >
          {submitting
            ? (paymentOption === 'send-link' ? 'Sending Link...' : 'Processing...')
            : (paymentOption === 'send-link' ? 'Create Booking & Send Link' : 'Process Payment Now')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Payment Link Dialog */}
    <Dialog
        open={paymentLinkDialog.open}
        onClose={() => {
          setPaymentLinkDialog({ open: false, link: '', bookingId: '', email: '' });
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney color="success" />
            <Typography variant="h6">Payment Link Generated</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment link has been generated successfully! Share this link with the customer.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Customer Email:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {paymentLinkDialog.email}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Payment Link:
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href={paymentLinkDialog.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                wordBreak: 'break-all',
                '&:hover': {
                  textDecoration: 'none',
                }
              }}
            >
              {paymentLinkDialog.link}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              navigator.clipboard.writeText(paymentLinkDialog.link);
              toast.success('Payment link copied to clipboard!');
            }}
            startIcon={<AttachMoney />}
          >
            Copy Payment Link
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentLinkDialog({ open: false, link: '', bookingId: '', email: '' });
              handleClose();
            }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guest Customer Dialog */}
      <GuestCustomerDialog
        open={guestCustomerDialogOpen}
        onClose={() => setGuestCustomerDialogOpen(false)}
        onConfirm={(guestCustomer) => {
          setSelectedCustomer(guestCustomer);
          setGuestCustomerDialogOpen(false);
        }}
      />

      {/* Past Date Confirmation Dialog */}
      <Dialog
        open={showPastDateConfirmation}
        onClose={() => setShowPastDateConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚠️ Confirm Past Date Booking
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to create a booking with a start date in the past.
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>Start Date:</strong> {startDate}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Past date bookings should only be created for:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2 }}>
            <li>Historical record-keeping</li>
            <li>Data migration purposes</li>
            <li>Backdating approved bookings</li>
          </Box>
          <Typography variant="body2" color="error">
            Are you sure you want to proceed with this past date booking?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPastDateConfirmation(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setShowPastDateConfirmation(false);
              performSubmit();
            }}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Yes, Create Past Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateVehicleBookingDialog;
